import { isJsonString } from '../typeCheckUtils';
import store from '../../store';
import i18n from '../../plugins/i18n';
import { normalizeJobKinds, getJobKindLabel } from '../aiJobUtils';

/**
 * AI 작업 메시지 구성
 * jobKindText와 eventDescription을 조합하여 메시지를 생성합니다.
 * 
 * @param {string} jobKindText - 작업 종류 텍스트 (예: "이미지 Geo-Localization")
 * @param {string} eventDescription - 이벤트 설명
 * @param {string} defaultMessageKey - eventDescription이 없을 때 사용할 i18n 키
 * @returns {string} 구성된 메시지
 */
const buildJobMessage = (jobKindText, eventDescription, defaultMessageKey) => {
  const defaultJobKindText = jobKindText || i18n.t('aiJob.ws.defaultJobName');
  const description = eventDescription || i18n.t(defaultMessageKey);
  return `${defaultJobKindText} ${description}`;
};

/**
 * AI 작업 실패 메시지 구성
 * jobKindText, eventDescription, failReason을 조합하여 실패 메시지를 생성합니다.
 * 
 * @param {string} jobKindText - 작업 종류 텍스트 (예: "이미지 Geo-Localization")
 * @param {string} eventDescription - 이벤트 설명
 * @param {string} failReason - 실패 사유
 * @returns {string} 구성된 실패 메시지
 */
const buildFailedJobMessage = (jobKindText, eventDescription, failReason) => {
  const defaultJobKindText = jobKindText || i18n.t('aiJob.ws.defaultJobName');
  const defaultDescription = eventDescription || i18n.t('aiJob.ws.jobFailedDefault');
  return `${defaultJobKindText} ${defaultDescription} ${i18n.t('aiJob.ws.failReasonPrefix')}${failReason}`;
};

/**
 * 메시지 타입별 처리 핸들러 맵 (최상위 객체)
 * 이 객체만 수정하면 처리 로직이 변경됨
 * 
 * key: messageType (Manager에서 분류된 타입)
 * value: 처리 함수 (topicPath, payload) => void
 */
const messageTypeHandlers = {
  /**
   * AI 작업 관련 메시지 처리
   * @param {string} topicPath - 토픽 경로
   * @param {unknown} payload - 메시지 본문
   */
  ai_job: (topicPath, payload) => {
    // 새로운 WebSocket 응답 형식: { eventType, eventDescription, eventData }
    const eventType = payload.eventType || payload.type || payload.messageType;
    const eventDescription = payload.eventDescription || '';
    const eventData = payload.eventData || payload.data || payload;
    const jobKinds = eventData?.jobKinds || [];
    
    // jobKinds를 로케일 라벨로 변환하여 ", "로 연결
    const normalized = normalizeJobKinds(jobKinds);
    const jobKindTextList = normalized
      .map(kind => getJobKindLabel(kind))
      .filter(text => text && text !== '-');

    let jobKindText = i18n.t('aiJob.ws.defaultJobName');
    if (jobKindTextList.length > 0) {
      jobKindText = jobKindTextList.join(', ');
    }
    
    const siteName = eventData?.siteName;
    const reportName = eventData?.reportName;
    
    let title = '';
        title =  `${siteName?siteName:''}${reportName?` [${reportName}]` : ''}`;
    
    let alertConfig = {};
    
    if (eventType === 'AI_JOB_STARTED' || eventType === 'ai_job_started') {
      const message = buildJobMessage(jobKindText, eventDescription, 'aiJob.ws.jobStarted');
      
      alertConfig = {
        type: 'info',
        title: title,
        message: message,
        timeout: 3000
      };
    } else if (eventType === 'AI_JOB_COMPLETED' || eventType === 'ai_job_completed') {
      const message = buildJobMessage(jobKindText, eventDescription, 'aiJob.ws.jobCompleted');
      
      alertConfig = {
        type: 'success',
        title: title,
        message: message,
        // 완료는 사용자가 닫을 때까지 유지 (자동 제거 시 놓치기 쉬움). 실패와 동일하게 timeout 0.
        timeout: 0
      };
    } else if (eventType === 'AI_JOB_FAILED' || eventType === 'ai_job_failed') {
      const failReason = eventData?.jobFailReason || i18n.t('aiJob.ws.unknownJobFailReason');
      const message = buildFailedJobMessage(jobKindText, eventDescription, failReason);
      
      alertConfig = {
        type: 'error',
        title: title,
        message: message,
        timeout: 0 // 수동 닫기
      };
    }
    
    if (alertConfig.type) {
      store.dispatch('commonAlert/addAlert', alertConfig);
    }
  },
  
  /**
   * 기본 메시지 처리 (그 외 모든 메시지)
   * @param {string} topicPath - 토픽 경로
   * @param {unknown} payload - 메시지 본문
   */
  default: (topicPath, payload) => {
    const timestamp = new Date().toLocaleTimeString();
    
    let message = '';
    if (typeof payload === 'object' && payload !== null) {
      message = payload.siteName || JSON.stringify(payload, null, 2);
    } else {
      const messageContent = isJsonString(payload) 
        ? JSON.stringify(JSON.parse(payload), null, 2) 
        : payload;
      message = messageContent;
    }
    
    store.dispatch('commonAlert/addAlert', {
      type: 'info',
      title: `[${timestamp}] ${topicPath}`,
      message: message,
      timeout: 6000
    });
  }
};

/**
 * 분류된 메시지 타입에 따라 적절한 핸들러 호출
 * (Manager에서 이미 분류가 완료된 상태로 받음)
 * 
 * @param {string} messageType - Manager에서 분류된 메시지 타입
 * @param {string} topicPath - 토픽 경로
 * @param {unknown} payload - 메시지 본문
 */
const handleByMessageType = (messageType, topicPath, payload) => {
  try {
    const handler = messageTypeHandlers[messageType] || messageTypeHandlers.default;
    handler(topicPath, payload);
    
  } catch (error) {
    console.error(`[TopicMessageHandler] 메시지 처리 오류 (타입: ${messageType}):`, error);
    store.dispatch('commonAlert/addAlert', {
      type: 'error',
      title: i18n.t('common.messageProcessingError'),
      message: error.message || i18n.t('common.unknownError'),
      timeout: 6000
    });
  }
};

/**
 * 새로운 메시지 타입 핸들러 등록
 * @param {string} messageType - 메시지 타입
 * @param {Function} handler - 핸들러 함수 (topicPath, payload) => void
 */
const registerMessageTypeHandler = (messageType, handler) => {
  messageTypeHandlers[messageType] = handler;
};

// 기존 함수는 호환성을 위해 유지 (선택사항)
const handleTopicMessage = (topicPath, payload) => {
  // 이 함수는 더 이상 사용하지 않음 (Manager에서 분류 후 handleByMessageType 호출)
  // 호환성을 위해 default 핸들러로 위임
  handleByMessageType('default', topicPath, payload);
};

const topicMessageHandler = {
  handleByMessageType,
  handleTopicMessage, // 호환성 유지
  registerMessageTypeHandler
};

export default topicMessageHandler;
